/**
 * Tour data validation.
 *
 * Tours are DATA — a file under ./tours/ exporting an array of steps. That is the whole point: the
 * owner's future sales-demo tour must be a new data file, not a second engine. The cost of data is
 * that a typo (`permision`, a missing `anchor`, two steps sharing an id) produces no compiler error,
 * so it is caught here instead, and caught at MODULE LOAD so a malformed tour cannot reach a user.
 *
 * Strictness is environment-dependent for one deliberate reason: a `throw` at import time in the
 * browser white-screens the app, and a broken help feature must never take the app down with it. So
 * dev/test throw (you find it on the first render, and CI's tour tests find it before that), while a
 * production build logs and drops the offending tour. `validateTour` itself is pure — it returns
 * errors and decides nothing — so the tests can assert on the messages without catching anything.
 */
import { ANCHOR_NAME_PATTERN } from './tourAnchors'
import { GATE_SIGNALS, ANCHOR_SIGNALS, MULTI_ANCHOR_SIGNALS } from './gateDetectors'

// MODE is 'production' only in a production build. Vitest runs with MODE 'test', vite dev with
// 'development' — both strict.
export const TOUR_STRICT = import.meta.env.MODE !== 'production'

/**
 * How a step hands control back to the tour.
 *
 * THE OWNER'S REQUIREMENT LIVES HERE: "It should be a guided tour. not something a use can spam next
 * on to get through." So a step declares which KIND it is, and the two kinds are treated differently
 * on purpose:
 *
 *   NEXT is an EXPLAIN step. There is nothing to do, so there is nothing to wait for, and gating it
 *   would be gating reading. Next advances.
 *
 *   CLICK and DO are ACTION steps. The person has to actually do the thing, and Next is DISABLED
 *   until they have. Somebody who clicks Next nine times and arrives at "you're all set" believes
 *   they have set up a machine, and the first they learn otherwise is a crew standing at an
 *   excavator that will not scan.
 *
 * An action step therefore also has to declare HOW THE ENGINE KNOWS IT HAPPENED — see `until` in
 * gateDetectors.js. CLICK is the common case written short: it means `until: { clicked: <anchor> }`.
 */
export const ADVANCE_ON = Object.freeze({
  /** The person clicks the highlighted element. Next does NOT stand in for it. */
  CLICK: 'click',
  /** Nothing to click (a heading, a status panel) — Next only. Reading is never gated. */
  NEXT: 'next',
  /** The person does something the engine can observe. Requires `until`. */
  DO: 'do',
})

const ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * Seeded role names (identity backend database/session.py) are snake_case — `master_admin`,
 * `office_staff` — not the kebab-case the ids use. A `role` written in the wrong shape would simply
 * never match and would silently hide the tour from everyone, so the shape is checked.
 */
const ROLE_PATTERN = /^[a-z][a-z0-9_]*$/

const STEP_KEYS = new Set([
  'id', 'anchor', 'title', 'body', 'route', 'screen', 'permission', 'module', 'role', 'advanceOn',
  // How the engine knows an action step happened, and the honest sentence to show if it never does.
  'until', 'stuckHint',
  // The earlier step a RESUMED run goes back to, because this one stands on what that one makes
  // (a practice record starts fresh after a reload — see TourProvider.startTour).
  'resumeFrom',
])
const TOUR_KEYS = new Set([
  // `permission` is the tour-level counterpart of the step-level one, and it is not a convenience:
  // a tour whose opening steps are plain navigation cannot be gated by its steps alone, because
  // those steps are ungated on purpose (everyone can see a nav tab) and therefore always survive the
  // filter. See availableTours for the stub that produces.
  'id', 'title', 'summary', 'audience', 'module', 'role', 'permission', 'steps',
  // A server MODULE SWITCH the tour cannot run without, beyond its own `module`. Capital estimates
  // live in Project Financials (`module: 'capex'`) but their server routes answer only while the
  // `capital` switch is on too, and `capital` has no nav tab of its own any more — so `module` alone
  // cannot say it, and a tour offered with it off would open on "Capital Projects is switched off".
  'requiresSwitch',
  // What the first card of a resumed run says about the practice record starting fresh.
  'resumeNote',
  // Practice run — see `practice` in defineTour. It DEFAULTS TO TRUE and opting out is explicit.
  'practice',
])

/** Audiences exist so the launcher can separate "teach me the app" from "show me the app". */
export const AUDIENCE = Object.freeze({ ONBOARDING: 'onboarding', DEMO: 'demo' })

/**
 * @returns {string[]} human-readable problems; empty means valid.
 */
export function validateTour(tour) {
  const errors = []
  const at = (msg) => errors.push(`tour "${tour && tour.id ? tour.id : '<no id>'}": ${msg}`)

  if (!tour || typeof tour !== 'object') return ['tour is not an object']
  if (!ID_PATTERN.test(tour.id || '')) at(`id ${JSON.stringify(tour.id)} must be kebab-case`)
  if (!tour.title) at('title is required — it is what the launcher lists')
  if (!Object.values(AUDIENCE).includes(tour.audience)) {
    at(`audience must be one of ${Object.values(AUDIENCE).join(' | ')}, got ${JSON.stringify(tour.audience)}`)
  }
  if (tour.role && !ROLE_PATTERN.test(tour.role)) {
    at(`role ${JSON.stringify(tour.role)} must be a lowercase seeded role name like "foreman"`)
  }
  for (const key of Object.keys(tour)) {
    // A key the engine does not read is almost always a typo for one it does, and a silently
    // ignored `permision` on a step is a permission gate that never fires.
    if (!TOUR_KEYS.has(key)) at(`unknown key "${key}" (did you misspell one of ${[...TOUR_KEYS].join(', ')}?)`)
  }
  validatePermission(tour.permission, at, 'the tour')
  if (tour.resumeNote != null && (typeof tour.resumeNote !== 'string' || !tour.resumeNote.trim())) {
    at('resumeNote must be the sentence a resumed run shows')
  }
  if (tour.requiresSwitch != null && !ROLE_PATTERN.test(String(tour.requiresSwitch))) {
    at(`requiresSwitch ${JSON.stringify(tour.requiresSwitch)} must be a module switch name like "capital"`)
  }

  if (!Array.isArray(tour.steps) || tour.steps.length === 0) {
    at('steps must be a non-empty array')
    return errors
  }

  const seenIds = new Set()
  const seenAnchors = new Set()
  tour.steps.forEach((step, i) => {
    const where = `step ${i} (${step && step.id ? step.id : 'no id'})`
    if (!step || typeof step !== 'object') { at(`${where} is not an object`); return }

    for (const key of Object.keys(step)) {
      if (!STEP_KEYS.has(key)) at(`${where}: unknown key "${key}"`)
    }

    if (!ID_PATTERN.test(step.id || '')) {
      at(`${where}: id must be kebab-case`)
    } else if (seenIds.has(step.id)) {
      // Progress is stored as a step ID, not an index — indices shift the moment a step is
      // permission-filtered out or someone inserts one. Duplicate ids would make a resume ambiguous.
      at(`${where}: duplicate step id "${step.id}" — progress is keyed on it`)
    } else {
      seenIds.add(step.id)
    }

    if (!ANCHOR_NAME_PATTERN.test(step.anchor || '')) {
      at(`${where}: anchor ${JSON.stringify(step.anchor)} must look like "daily-report.submit"`)
    } else if (seenAnchors.has(step.anchor)) {
      at(`${where}: anchor "${step.anchor}" is already used by an earlier step in this tour`)
    } else {
      seenAnchors.add(step.anchor)
    }

    if (step.role && !ROLE_PATTERN.test(step.role)) {
      at(`${where}: role ${JSON.stringify(step.role)} must be a lowercase seeded role name`)
    }
    validatePermission(step.permission, at, where)
    if (step.resumeFrom != null) {
      const earlier = tour.steps.slice(0, i).map((x) => x && x.id)
      if (!earlier.includes(step.resumeFrom)) {
        at(`${where}: resumeFrom ${JSON.stringify(step.resumeFrom)} must name an EARLIER step of this tour`)
      }
    }
    if (!step.title) at(`${where}: title is required`)
    if (!step.body) at(`${where}: body is required — a step with no sentence teaches nothing`)
    if (step.route && !step.route.startsWith('/')) at(`${where}: route must be an absolute path`)
    if (step.advanceOn && !Object.values(ADVANCE_ON).includes(step.advanceOn)) {
      at(`${where}: advanceOn must be ${Object.values(ADVANCE_ON).join(' | ')}`)
    }
    if (step.stuckHint != null && typeof step.stuckHint !== 'string') {
      at(`${where}: stuckHint must be the sentence to show someone who cannot do this step`)
    }
    validateUntil(step, at, where)

    // The `requested` signal listens to practiceMode's request bus, and that bus only carries
    // anything while a practice run is armed (practiceMode.js installs the transport wrappers on
    // arm and takes them off on disarm). So this gate on an opted-out tour never fires: the step is
    // a permanent dead end whose only exit is waiting out the stuck timer. It is silent, it is
    // fifteen seconds of nothing per occurrence, and it is invisible in review — so it is refused
    // here instead.
    if (tour.practice === false && step.until && step.until.requested) {
      at(`${where}: until.requested cannot fire on a tour with practice:false — the request bus only runs during a practice run. Use a DOM signal (appears/gone/filled) or drop the opt-out.`)
    }
  })

  return errors
}

/**
 * A permission is one grant, or a LIST OF GRANTS THAT ARE ALL NEEDED.
 *
 * ALL, never any. Some controls stand on several grants at once — the capital award is Edit Bids AND
 * Create Projects AND Manage Invoices, because it writes a contract value and a budget — and a step
 * pointing at one may only be offered to somebody who holds every one of them, or it spotlights a
 * button the screen has greyed out. "Any of" is deliberately not offered (see equipment-sticker.js):
 * it is the shape that offers a step to somebody who cannot do it.
 *
 * An empty list is refused rather than read as "no gate": a step that silently stopped checking a
 * permission is invisible in review.
 */
const PERMISSION_PATTERN = /^[a-z_]+:[a-z_]+$/

function validatePermission(permission, at, where) {
  if (permission == null) return
  const list = [].concat(permission)
  if (list.length === 0) {
    at(`${where}: permission is an empty list — name the grants it needs, or drop the key`)
    return
  }
  for (const p of list) {
    if (typeof p !== 'string' || !PERMISSION_PATTERN.test(p)) {
      at(`${where}: permission ${JSON.stringify(p)} must look like "bid:edit_bid"`)
    }
  }
}

/**
 * `until` is the whole difference between a guided tour and a slideshow, so a malformed one is not
 * allowed to degrade quietly into "no gate": a step that silently stopped gating is exactly the
 * spam-Next behaviour this was built to remove, and nobody would notice for months.
 */
function validateUntil(step, at, where) {
  const isDo = step.advanceOn === ADVANCE_ON.DO

  if (step.until == null) {
    if (isDo) at(`${where}: advanceOn "do" needs an "until" saying how the engine knows they did it — one of ${GATE_SIGNALS.join(', ')}`)
    return
  }

  if (step.advanceOn === ADVANCE_ON.NEXT) {
    at(`${where}: an "until" on a NEXT step is never consulted — either drop it or make this an action step`)
    return
  }
  if (typeof step.until !== 'object' || Array.isArray(step.until)) {
    at(`${where}: until must be an object like { appears: "fleet.stickers.dialog" }`)
    return
  }

  const keys = Object.keys(step.until)
  const known = keys.filter((k) => GATE_SIGNALS.includes(k))
  const unknown = keys.filter((k) => !GATE_SIGNALS.includes(k))
  for (const k of unknown) at(`${where}: until has unknown signal "${k}" (known: ${GATE_SIGNALS.join(', ')})`)
  if (known.length === 0) {
    at(`${where}: until must carry one of ${GATE_SIGNALS.join(', ')}`)
    return
  }
  if (known.length > 1) {
    // Two signals would mean "whichever happens first", which reads as a mistake far more often than
    // it reads as intent, and is unfalsifiable in the anchor guard.
    at(`${where}: until carries ${known.length} signals (${known.join(', ')}) — a step waits for exactly one thing`)
    return
  }

  const signal = known[0]
  const target = step.until[signal]

  /**
   * A LIST OF ANCHORS — only `answered`, and checked here so a card cannot quietly gate on fewer
   * boxes than its author meant. Every failure this validates for is silent at runtime: a duplicate
   * name is a question counted twice and another one not counted at all, an empty list is a gate that
   * can never satisfy (nothing is on the page), and a typo'd anchor is a question the reader is held
   * for forever. All three read as "the tour is stuck", which is indistinguishable from the app being
   * broken.
   */
  if (Array.isArray(target)) {
    if (!MULTI_ANCHOR_SIGNALS.includes(signal)) {
      at(`${where}: until.${signal} takes ONE target — only ${MULTI_ANCHOR_SIGNALS.join(', ')} may name a set`)
      return
    }
    if (target.length === 0) {
      at(`${where}: until.${signal} is an empty list — a gate that names no question can never be satisfied`)
      return
    }
    const seen = new Set()
    for (const one of target) {
      if (typeof one !== 'string' || !ANCHOR_NAME_PATTERN.test(one)) {
        at(`${where}: until.${signal} ${JSON.stringify(one)} must be an anchor name like "forms.fill.field.crew"`)
      } else if (seen.has(one)) {
        at(`${where}: until.${signal} names "${one}" twice`)
      } else {
        seen.add(one)
      }
    }
    return
  }

  if (typeof target !== 'string' || target.length === 0) {
    at(`${where}: until.${signal} must be a non-empty string`)
  } else if (ANCHOR_SIGNALS.includes(signal) && !ANCHOR_NAME_PATTERN.test(target)) {
    at(`${where}: until.${signal} ${JSON.stringify(target)} must be an anchor name like "daily-report.submit"`)
  } else if (signal === 'route' && !target.startsWith('/')) {
    at(`${where}: until.route must be an absolute path`)
  } else if (signal === 'requested' && !/^(GET|POST|PUT|PATCH|DELETE)\s+\S/.test(target)) {
    at(`${where}: until.requested must read like "POST /forms-settings/stickers"`)
  }
}

/**
 * Normalize + validate one tour. Every data file calls this, so a tour that reaches the registry has
 * already been checked.
 */
export function defineTour(tour) {
  const errors = validateTour(tour)
  if (errors.length > 0) {
    const message = `Invalid tour definition:\n  - ${errors.join('\n  - ')}`
    if (TOUR_STRICT) throw new Error(message)
    console.error(message)
    return null
  }

  return Object.freeze({
    ...tour,
    /**
     * PRACTICE DEFAULTS TO TRUE, AND THAT DEFAULT IS THE SAFETY PROPERTY.
     *
     * Every tour written so far walks somebody through creating something real — a daily report that
     * mints field tickets, time entries and posted cost; a sticker that burns a code and puts a label
     * on a machine; a PIN that overwrites the one a working field hand is using today. If `practice`
     * defaulted to false, the way to get a durable side effect from a training exercise would be to
     * FORGET a line, and forgetting is not a thing you can review for.
     *
     * Opting out has to be typed (`practice: false`), which is a thing a reviewer can see.
     */
    practice: tour.practice !== false,
    steps: Object.freeze(tour.steps.map((s) => {
      const advanceOn = s.advanceOn || (s.until ? ADVANCE_ON.DO : ADVANCE_ON.CLICK)
      return Object.freeze({
        ...s,
        advanceOn,
        // The harness key for the anti-rot guard. Defaults to the route, which is right for every
        // screen that HAS a route; `screen` exists for the ones that do not (HR keeps its tab in
        // useState, so /hr is four different screens at one URL).
        screen: s.screen || s.route || null,
        // CLICK is `until: { clicked: <this step's anchor> }` written short — the overwhelmingly
        // common action step. Normalising it here means the overlay has ONE code path for gating
        // instead of a special case that can drift away from the general one.
        until: advanceOn === ADVANCE_ON.NEXT
          ? null
          : s.until || { clicked: s.anchor },
      })
    })),
  })
}

/** Does this step wait for the person to do something, rather than for them to finish reading? */
export const isActionStep = (step) => !!step && step.advanceOn !== ADVANCE_ON.NEXT
